import { buildUserInfo, decodeAccessTokenPayload, decodeIdTokenPayload, parseStoredUserProfile } from "./jwt";
import { STORAGE_KEYS } from "./storage";
import type { AuthResult, AuthStrategy, StorageAdapter, TokenSet, UserInfo } from "./types";

const SNAPSHOT_VERSION = 1;
const MAX_SESSION_SNAPSHOT_BYTES = 65_536;

interface SessionSnapshot {
  version: typeof SNAPSHOT_VERSION;
  epoch: string;
  revision: number;
  tokenSet: TokenSet;
  profile: Pick<UserInfo, "sub" | "realmId" | "tenantId" | "email" | "name" | "picture">;
}

export type SessionSnapshotRead =
  | { kind: "missing" }
  | { kind: "invalid" }
  | { kind: "valid"; epoch: string; revision: number; result: AuthResult };

export interface SessionSnapshotVersion {
  epoch: string;
  revision: number;
}

export class StaleSessionSnapshotError extends Error {
  constructor() {
    super("Session snapshot changed while the operation was in flight");
    this.name = "StaleSessionSnapshotError";
  }
}

export class SessionSnapshotStore {
  constructor(
    private readonly storage: StorageAdapter,
    private readonly provider: AuthStrategy,
  ) {}

  read(): SessionSnapshotRead {
    const encoded = this.storage.getItem(STORAGE_KEYS.sessionSnapshot);
    if (encoded === null) return { kind: "missing" };
    if (byteLength(encoded) > MAX_SESSION_SNAPSHOT_BYTES) return { kind: "invalid" };

    try {
      const raw = JSON.parse(encoded) as unknown;
      if (typeof raw !== "object" || raw === null) return { kind: "invalid" };
      const candidate = raw as Record<string, unknown>;
      if (
        candidate.version !== SNAPSHOT_VERSION ||
        !isValidEpoch(candidate.epoch) ||
        !Number.isSafeInteger(candidate.revision) ||
        typeof candidate.revision !== "number" ||
        candidate.revision <= 0 ||
        typeof candidate.tokenSet !== "object" ||
        candidate.tokenSet === null
      ) {
        return { kind: "invalid" };
      }

      const tokenSet = this.provider.validateTokenSet(candidate.tokenSet as TokenSet, { allowExpired: true });
      const user = restoreUser(tokenSet, candidate.profile);
      if (!user) return { kind: "invalid" };
      return { kind: "valid", epoch: candidate.epoch, revision: candidate.revision, result: { tokenSet, user } };
    } catch {
      return { kind: "invalid" };
    }
  }

  writeNew(result: AuthResult, epoch: string): SessionSnapshotVersion & { result: AuthResult } {
    if (!isValidEpoch(epoch)) throw new Error("Invalid session epoch");
    return this.persist(result, { epoch, revision: 1 });
  }

  write(result: AuthResult, expected: SessionSnapshotVersion): SessionSnapshotVersion & { result: AuthResult } {
    const current = this.read();
    if (current.kind !== "valid" || current.epoch !== expected.epoch || current.revision !== expected.revision) {
      throw new StaleSessionSnapshotError();
    }

    return this.persist(result, { epoch: expected.epoch, revision: nextRevision(current.revision) });
  }

  private persist(
    result: AuthResult,
    version: SessionSnapshotVersion,
  ): SessionSnapshotVersion & { result: AuthResult } {
    const tokenSet = this.provider.validateTokenSet(result.tokenSet);
    const tokenUser = restoreUser(tokenSet, result.user);
    if (!tokenUser || !hasSameUserBinding(tokenUser, result.user)) {
      throw new Error("Session profile does not match validated token claims");
    }
    const user = mergeProfile(tokenUser, result.user);
    const snapshot: SessionSnapshot = {
      version: SNAPSHOT_VERSION,
      epoch: version.epoch,
      revision: version.revision,
      tokenSet,
      profile: {
        sub: user.sub,
        realmId: user.realmId,
        tenantId: user.tenantId,
        email: user.email,
        name: user.name,
        picture: user.picture,
      },
    };
    const encoded = JSON.stringify(snapshot);
    if (byteLength(encoded) > MAX_SESSION_SNAPSHOT_BYTES) {
      throw new Error("Session snapshot exceeds size limit");
    }
    this.storage.setItem(STORAGE_KEYS.sessionSnapshot, encoded);
    return { epoch: version.epoch, revision: version.revision, result: { tokenSet, user } };
  }

  clear(epoch?: string): void {
    if (epoch === undefined) {
      this.storage.removeItem(STORAGE_KEYS.sessionSnapshot);
      return;
    }
    const current = this.read();
    if (current.kind === "valid" && current.epoch === epoch) {
      this.storage.removeItem(STORAGE_KEYS.sessionSnapshot);
    }
  }
}

function restoreUser(tokenSet: TokenSet, profile: unknown): UserInfo | null {
  const accessPayload = decodeAccessTokenPayload(tokenSet.accessToken);
  const idPayload = tokenSet.idToken ? decodeIdTokenPayload(tokenSet.idToken) : null;
  if (!accessPayload || (tokenSet.idToken && !idPayload)) return null;
  const tokenUser = buildUserInfo(idPayload, accessPayload);
  if (!tokenUser) return null;
  const storedProfile = parseStoredUserProfile(profile);
  if (!storedProfile || !hasSameUserBinding(tokenUser, storedProfile)) return null;
  return mergeProfile(tokenUser, storedProfile);
}

function mergeProfile(tokenUser: UserInfo, profile: Pick<UserInfo, "email" | "name" | "picture">): UserInfo {
  return {
    ...tokenUser,
    email: tokenUser.email ?? profile.email,
    name: tokenUser.name ?? profile.name,
    picture: tokenUser.picture ?? profile.picture,
  };
}

function hasSameUserBinding(
  left: Pick<UserInfo, "sub" | "realmId" | "tenantId">,
  right: Pick<UserInfo, "sub" | "realmId" | "tenantId">,
): boolean {
  return left.sub === right.sub && left.realmId === right.realmId && left.tenantId === right.tenantId;
}

function nextRevision(revision: number): number {
  if (revision >= Number.MAX_SAFE_INTEGER) throw new Error("Session snapshot revision exhausted");
  return revision + 1;
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function isValidEpoch(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value);
}
